'use client';

import React, { useState, useEffect, useRef } from "react";
import { GoChevronDown } from "react-icons/go";
import { useGetCountries } from "../../api/countryCode";
import Image from "../Image";

// Country phone number length mapping (phonecode -> expected length)
export const COUNTRY_PHONE_LENGTHS = {
  "91": 10, // India
  "1": 10, // USA, Canada
  "44": 10, // UK
  "61": 9, // Australia
  "81": 10, // Japan
  "86": 11, // China
  "49": 10, // Germany
  "33": 9, // France
  "39": 10, // Italy
  "34": 9, // Spain
  "7": 10, // Russia
  "82": 10, // South Korea
  "55": 11, // Brazil
  "52": 10, // Mexico
  "27": 9, // South Africa
  "31": 9, // Netherlands
  "46": 9, // Sweden
  "47": 8, // Norway
  "45": 8, // Denmark
  "358": 9, // Finland
  "971": 9, // UAE
  "966": 9, // Saudi Arabia
  "65": 8, // Singapore
  "60": 9, // Malaysia
  "62": 10, // Indonesia
  "63": 10, // Philippines
  "66": 9, // Thailand
  "84": 10, // Vietnam
  "880": 10, // Bangladesh
  "92": 10, // Pakistan
  "94": 9, // Sri Lanka
  "977": 10, // Nepal
};

const PhoneNumberInput = ({
  label = "Input",
  name,
  value = "",
  type = "text",
  placeholder = "",
  className = "",
  setFieldValue,
  onChange,
  error = "",
  countryId,
  defaultPhoneCode,
  ...props
}) => {
  const { data, isPending } = useGetCountries();
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    if (data) {
      setCountries(data.countryData);
    }
  }, [data]);

  const getDefaultCountry = () => {
    if (defaultPhoneCode) {
      const match = countries.find((c) => c.phonecode == defaultPhoneCode);
      if (match) return match;
    }
    return (
      countries.find((c) => c.phonecode == 1 && /united states/i.test(c.name)) ||
      countries.find((c) => c.phonecode == 1) ||
      countries[0]
    );
  };

  const parseCountryAndNumber = (val, currentCountry) => {
    if (!val) return { country: currentCountry || getDefaultCountry(), number: "" };
    const parts = val.split(" ");
    const countryCode = parts[0].replace("+", "");
    const country =
      (currentCountry?.phonecode == countryCode ? currentCountry : null) ||
      countries.find((c) => c.phonecode == countryCode) ||
      currentCountry ||
      getDefaultCountry();
    // Filter out non-numeric characters from the phone number
    let number = parts.slice(1).join(" ").replace(/\D/g, '');
    
    // Limit length based on country phone code
    const code = country?.phonecode?.toString();
    const maxLength = COUNTRY_PHONE_LENGTHS[code];
    if (maxLength && number.length > maxLength) {
      number = number.slice(0, maxLength);
    }
    
    return { country, number };
  };

  const [selectedCountry, setSelectedCountry] = useState(
    () => parseCountryAndNumber(value, null).country
  );
  const [phoneNumber, setPhoneNumber] = useState(
    () => parseCountryAndNumber(value, selectedCountry).number
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchTerm("");
    
    // Trim phone number if it exceeds the new country's max length
    const countryCode = country?.phonecode?.toString();
    const maxLength = COUNTRY_PHONE_LENGTHS[countryCode];
    let trimmedNumber = phoneNumber;
    if (maxLength && phoneNumber.length > maxLength) {
      trimmedNumber = phoneNumber.slice(0, maxLength);
      setPhoneNumber(trimmedNumber);
    }
    
    if (setFieldValue) {
      setFieldValue(name, `+${country.phonecode} ${trimmedNumber}`);
    }
  };

  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    // Only allow numeric characters (0-9)
    let newPhoneNumber = inputValue.replace(/\D/g, '');
    
    // Limit length based on country phone code
    const countryCode = selectedCountry?.phonecode?.toString();
    const maxLength = COUNTRY_PHONE_LENGTHS[countryCode];
    if (maxLength && newPhoneNumber.length > maxLength) {
      newPhoneNumber = newPhoneNumber.slice(0, maxLength);
    }
    
    setPhoneNumber(newPhoneNumber);
    if (setFieldValue) {
      setFieldValue(name, `+${selectedCountry.phonecode} ${newPhoneNumber}`);
    }
  };

  useEffect(() => {
    const { country, number } = parseCountryAndNumber(value, selectedCountry);
    setSelectedCountry(country);
    setPhoneNumber(number);
  }, [value, countries]);

  // Sync phone-code with parent-supplied country selection. Skip the very first
  // run so saved/edit-mode data isn't overwritten — only react to *user changes*
  // of the country picker after mount.
  const prevCountryIdRef = useRef(countryId);
  useEffect(() => {
    if (prevCountryIdRef.current === countryId) return;
    prevCountryIdRef.current = countryId;
    if (!countryId || countries.length === 0) return;
    const match = countries.find((c) => String(c.id) === String(countryId));
    if (!match || match.phonecode == null) return;
    if (selectedCountry && String(selectedCountry.id) === String(match.id)) return;
    setSelectedCountry(match);
    const maxLength = COUNTRY_PHONE_LENGTHS[match.phonecode?.toString()];
    const trimmed = maxLength && phoneNumber.length > maxLength
      ? phoneNumber.slice(0, maxLength)
      : phoneNumber;
    if (trimmed !== phoneNumber) setPhoneNumber(trimmed);
    if (setFieldValue) {
      setFieldValue(name, `+${match.phonecode} ${trimmed}`);
    }
  }, [countryId, countries]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  if (isPending) {
    return <div className="bg-color-95 animate-pulse rounded-md h-9"></div>;
  }

  const isDisabled = props.disabled;

  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.phonecode.toString().includes(searchTerm)
  );

  return (
    <div className="relative w-full flex gap-0 justify-start group/nested">
      <div ref={dropdownRef}>
        <button
          type="button"
          tabIndex={props.tabIndex}
          onClick={() => !isDisabled && setIsDropdownOpen(!isDropdownOpen)}
          disabled={isDisabled}
          className={`peer w-24 bg-color-98 text-color-1 text-sm border border-color-50 border-r-0 rounded-e-none rounded-md px-2 py-2 flex items-center justify-between 
            transition duration-300 ease focus:outline-none 
            focus:border-[var(--accent-dark)] group-hover/nested:border-[var(--accent-dark)] shadow-sm focus:shadow ${
              isDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
        >
          <div className="flex items-center">
            <Image
              src={selectedCountry?.flag}
              alt={`${selectedCountry?.name} Flag`}
              className="w-5 h-5 mr-2 object-contain"
            />
            (+{selectedCountry?.phonecode})
          </div>
          <GoChevronDown className="font-thin text-sm" />
        </button>
        {isDropdownOpen && !isDisabled && (
          <ul
            className="absolute top-full left-0 mt-0 bg-color-98 border border-color-97 rounded-md shadow-md w-64 z-20 max-h-48 overflow-auto"
            role="menu"
          >
            <li className="sticky top-0 bg-color-98 p-2 border-b border-color-97 z-30">
              <input
                type="text"
                placeholder="Search country..."
                className="w-full px-2 py-1 text-sm bg-color-97 border border-color-50 rounded focus:outline-none focus:border-[var(--accent-dark)] text-color-1"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </li>
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country, index) => (
                <li key={index}>
                  <button
                    type="button"
                    onClick={() => handleCountryChange(country)}
                    className="flex items-center px-4 py-2 text-sm w-full hover:bg-color-97 focus:outline-none"
                  >
                    <Image
                      src={country.flag}
                      alt={`${country.name} Flag`}
                      className="w-5 h-5 mr-2 object-contain"
                    />
                    {country.name} (+{country.phonecode})
                  </button>
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-sm text-color-1 opacity-50">
                No countries found
              </li>
            )}
          </ul>
        )}
      </div>
      <div className="relative w-full">
        <input
          {...props}
          id={name}
          name={name}
          type={type}
          placeholder={placeholder || " "}
          value={phoneNumber}
          maxLength={COUNTRY_PHONE_LENGTHS[selectedCountry?.phonecode?.toString()] || 15}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
          className={`peer w-full bg-color-98 text-color-1 text-sm rounded-s-none border rounded-md px-3 py-2 
            transition duration-300 ease focus:outline-none 
            focus:border-[var(--accent-dark)] group-hover/nested:border-[var(--accent-dark)] shadow-sm focus:shadow ${
              error ? "border-red-500" : "border-color-50"
            } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
          onChange={handlePhoneChange}
        />
        <label
          htmlFor={name}
          className={`absolute -top-2 left-2.5 bg-color-98 px-1 text-xs text-color-1 w-fit ${
            error ? "text-red-500" : ""
          }`}
        >
          {label}
        </label>
      </div>
    </div>
  );
};

export default PhoneNumberInput;
